import * as React from "react";
import { useParams } from "react-router-dom";
import { useIsMutating } from "@tanstack/react-query";
import {
    Dialog,
    DialogContent,
    Button,
    Card,
    ActionButton,
    Badge,
} from "@jhool-io/fe-components";
import { useFetchClientBasicAndPersonalInfo } from "../../../../hooks/queries/client";
import ListState from "../../../ListState/ListState";
import EditPersonalInfo from "./EditPersonalInfo/EditPersonalInfo";
import { UserPermisions, UserRole } from "../../../../utils/types/user";
import {
    useDoesUserHavePermission,
    useDoesUserHaveRole,
} from "../../../../hooks/permissions";
import EditIcon from "../../../../icons/Edit";
import Loader from "../../../../shared-ui/Loader/Loader";
import PlusIcon from "../../../../icons/Plus";

export default function PersonalInfo() {
    // Component local states
    const [showModal, setShowModal] = React.useState(false);

    // Get id from url params
    const params = useParams();
    const clientId = params.clientId as string;

    // Fetch client personal info
    const clientPersonalInfo = useFetchClientBasicAndPersonalInfo(clientId);

    const handleToggleShowModal = () => {
        setShowModal((state) => !state);
    };

    // To determine if mutation is currently running, returns 0 || 1
    const isMutating = useIsMutating(["edit-personal-info", clientId]);

    // Roles restricted from editing
    const restrictedRoles = [UserRole.AUDITOR, UserRole.PROVIDER];

    const isAuditorAdmin = useDoesUserHaveRole([UserRole.AUDITOR_ADMIN]);

    // Check permissions and roles
    const hasEditPermission = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
        UserPermisions.CLIENT_INFO_PERSONAL_WRITE,
        UserPermisions.CLIENT_INFO_ALL_WRITE,
    ]);

    const hasRestrictedRole =
        useDoesUserHaveRole(restrictedRoles) && !isAuditorAdmin;
    const isSuperAdmin = useDoesUserHaveRole([UserRole.SUPER_ADMIN]);

    // Check if the logged in user can edit personal info
    const canEditPersonalInfo =
        hasEditPermission && (!hasRestrictedRole || isSuperAdmin);

    return (
        <>
            {clientPersonalInfo.data && (
                <Dialog open={showModal && Boolean(clientPersonalInfo.data)}>
                    <DialogContent
                        title="Edit info"
                        variant="center"
                        handleCloseDialog={handleToggleShowModal}
                        showFooter
                        cancelText="Cancel"
                        saveText="Save Changes"
                        submitBtnFormValue="edit-personal-info"
                        isCancelBtnDisabled={isMutating > 0}
                        isSubmitBtnDisabled={isMutating > 0}
                    >
                        <EditPersonalInfo
                            clientPersonalInfo={clientPersonalInfo.data.data}
                            onFormSubmit={handleToggleShowModal}
                        />
                    </DialogContent>
                </Dialog>
            )}

            {clientPersonalInfo.data && (
                <div>
                    {canEditPersonalInfo && !clientPersonalInfo.error && (
                        <Card classNames="flex justify-end mb-[--spacing-header]">
                            <ActionButton
                                onClick={handleToggleShowModal}
                                className="hidden md:flex"
                                actionTitle={
                                    clientPersonalInfo.data.data
                                        .relation_info &&
                                    clientPersonalInfo.data.data.relation_info
                                        .length === 0
                                        ? "Add relations info"
                                        : "Edit relations info"
                                }
                            >
                                {clientPersonalInfo.data.data.relation_info &&
                                clientPersonalInfo.data.data.relation_info
                                    .length === 0
                                    ? "Add relation"
                                    : "Edit"}
                            </ActionButton>
                            <Button
                                variant="primary"
                                onClick={handleToggleShowModal}
                                className="flex md:hidden"
                            >
                                {clientPersonalInfo.data.data.relation_info &&
                                clientPersonalInfo.data.data.relation_info
                                    .length === 0 ? (
                                    <PlusIcon />
                                ) : (
                                    <EditIcon />
                                )}
                            </Button>
                        </Card>
                    )}
                    <Card classNames="bg-[--bg-sidebar-dropdown-open]">
                        {clientPersonalInfo.isLoading && <Loader />}
                        {clientPersonalInfo.isError && (
                            <ListState
                                isError
                                errorMsg={
                                    clientPersonalInfo?.error?.response?.data
                                        .message ||
                                    `Cannot display client relation at this time please try again later`
                                }
                                stateHelperText="Try reloading this page to solve this issue"
                                context="general"
                            />
                        )}
                        {clientPersonalInfo.data &&
                            clientPersonalInfo.data.data.relation_info &&
                            clientPersonalInfo.data.data.relation_info
                                .length === 0 && (
                                <ListState
                                    stateHelperText="Client's relation info will appear here when added"
                                    emptyMessage="No relation info"
                                    context="general"
                                    isError={false}
                                />
                            )}
                        {clientPersonalInfo.data &&
                            clientPersonalInfo.data.data.relation_info &&
                            clientPersonalInfo.data.data.relation_info?.map(
                                (item) => (
                                    <div
                                        className="grid w-full gap-[--spacing-items] grid-cols-[repeat(auto-fit,minmax(150px,1fr))] mb-[--spacing-items]"
                                        key={item.relation_id}
                                    >
                                        <div>
                                            <span className="text-caption text-textSec">
                                                Relation type
                                                <span className="inline-block ml-1">
                                                    ({item.relationship_type})
                                                </span>
                                            </span>
                                            <h3 className="!text-text font-semibold text-base capitalize mt-1">
                                                <span className="inline-block mr-1">
                                                    {item.last_name}
                                                </span>
                                                <span>{item.first_name}</span>
                                            </h3>
                                        </div>
                                        <div className="flex flex-col break-words w-auto">
                                            <span className="text-caption text-textSec flex items-center gap-x-0.5">
                                                Contact
                                                {item.is_primary_contact && (
                                                    <Badge
                                                        content="primary"
                                                        type="danger"
                                                    />
                                                )}
                                            </span>
                                            <span className="!text-text font-semibold text-base mt-1 capitalize">
                                                {item.contact_phone_no}
                                            </span>
                                        </div>
                                    </div>
                                )
                            )}
                    </Card>
                </div>
            )}
        </>
    );
}
